import { useEffect } from 'react';
import type { To } from 'react-router';
import { matchPath, useMatch } from 'react-router';

import * as routes from '#src/constants/__generated__/routes.ts';
import { matchAnySportPath } from '#src/lib/routing/generateSportPath.ts';

import { useFeatureFlags } from './useFeatureFlags.tsx';
import { STORAGE, useInMemoryStorage } from './useInMemoryStorage.ts';
import { LIFECYCLE, useLifecycle } from './useLifecycle.ts';

export const useContextNavigation = ({ to }: { to?: To }) => {
  const [savedHistory, setSavedHistory] = useInMemoryStorage<
    Record<string, string>
  >(STORAGE.savedHistory, {});
  const canBeSaved = typeof to === 'string';
  const { pathname } = useMatch(`${to}/*`) || {};
  const savedItem = canBeSaved && savedHistory?.[to];

  useEffect(() => {
    if (canBeSaved && pathname && pathname !== savedItem) {
      setSavedHistory(saved => ({ ...saved, [to]: pathname }));
    }
  });

  if (canBeSaved && !pathname) {
    return savedItem;
  }
  return undefined;
};

const ROUTE_PATTERNS = Object.values(routes);

const knownRouteCache = new Map<string, boolean>();

const isKnownRoute = (pathname: string) => {
  const cached = knownRouteCache.get(pathname);
  if (cached !== undefined) {
    return cached;
  }

  const known = ROUTE_PATTERNS.some(
    pattern =>
      matchPath(pattern, pathname) || matchAnySportPath(pattern, pathname)
  );
  knownRouteCache.set(pathname, known);
  return known;
};

/** Similar to context mode navigation but only for the first subpath level (tabs)
 *  and is stored in the user's lifecycle instead of in-memory
 **/
export const useStoredContextNavigation = ({ to }: { to?: To }) => {
  const {
    flags: { useStoredLastNavigation = false },
  } = useFeatureFlags();
  const { lifecycle, update } = useLifecycle();
  const canBeSaved = typeof to === 'string';
  const enabledStoredLastNavigation = useStoredLastNavigation && canBeSaved;
  const { params } = useMatch(`${to}/:subpath/*`) || {};
  const pathname = params?.subpath && `${to}/${params?.subpath}`;
  const savedItem =
    canBeSaved && lifecycle?.[LIFECYCLE.storedLastNavigation]?.[to];

  useEffect(() => {
    if (
      enabledStoredLastNavigation &&
      pathname &&
      pathname !== savedItem &&
      isKnownRoute(pathname)
    ) {
      update(LIFECYCLE.storedLastNavigation, {
        ...lifecycle?.[LIFECYCLE.storedLastNavigation],
        [to]: pathname,
      });
    }
  });

  if (
    enabledStoredLastNavigation &&
    !pathname &&
    typeof savedItem === 'string' &&
    isKnownRoute(savedItem)
  ) {
    return savedItem;
  }
  return undefined;
};
